import { useCallback, useRef, useState } from 'react';
import type { IDashboardProject } from 'models';

/**
 * Native HTML5 drag-reorder for the default (un-pinned) project list (§3.E
 * "Drag-reorder" / parity 69-70). Replaces the Backbone jQuery-UI sortable on
 * `.list-rows-default` (axis y, disabled when `.no-drag` is present — i.e. any
 * filter active).
 *
 * Faithful behavior (container.js `reorder_projects` / projects.js):
 *  - reordering only the default `.project-li.drag` rows;
 *  - on drop, recompute each row's 1-indexed `dashboard_order` by DOM order;
 *  - no-op if nothing actually moved or fewer than 2 rows changed;
 *  - the FIRST manual reorder switches the user's
 *    `dashboard_projects_default_order` to 'manual' (clears the active column
 *    sort highlight; infobar shows "User Custom Sorted"); subsequent reorders
 *    just persist the order;
 *  - persist via `setCustomOrder` POST custom_order with
 *    `{project_id, dashboard_order}` entries sorted ascending by order.
 *
 * This hook owns ONLY the local drag-ordering of the rows it is given; the
 * caller supplies `onCommit` to perform the user 'manual' PATCH (first time) +
 * the custom_order POST. The hook exposes `order` (the live, possibly-reordered
 * id list to render) and the per-row drag props.
 */
export interface DragReorderResult {
  /** The rows to render in (possibly drag-reordered) order. */
  rows: IDashboardProject[];
  /** Drag handlers to spread onto each row's `<li>`. */
  dragProps: (id: number) => {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
  /** The id currently being dragged (for the `.dragging` class), or null. */
  draggingId: number | null;
}

interface UseRowDragReorderArgs {
  /** The default-list rows in their current (server / accumulated) order. */
  rows: IDashboardProject[];
  /** Disabled when a filter is active (`.no-drag` rows). */
  disabled: boolean;
  /**
   * Persist the new order. Receives the ordered list of `{project_id,
   * dashboard_order}` (1-indexed, sorted asc). Should perform the first-time
   * 'manual' switch + the custom_order POST.
   */
  onCommit: (entries: { project_id: number; dashboard_order: number }[]) => void;
}

export function useRowDragReorder({
  rows,
  disabled,
  onCommit,
}: UseRowDragReorderArgs): DragReorderResult {
  // Local override order (array of ids) applied on top of `rows`; null = follow
  // the server order. Reset implicitly when `rows` identity changes via the
  // signature guard below.
  const [orderIds, setOrderIds] = useState<number[] | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const dragIdRef = useRef<number | null>(null);

  // Track the server-row signature; when it changes decide whether to drop the
  // local override. A pure APPEND (infinite-scroll page: the previous ids are a
  // prefix of the new ids) KEEPS the override — a drag-reorder no longer
  // refetches the lists (QA #4, legacy reorder_projects only re-renders the
  // infobar), so `rows` may still hold the pre-drag order when the next page
  // lands; dropping the override there would snap the dragged rows back.
  // (Legacy parity: the jQuery-sortable DOM order survives a scroll append —
  // new rows are appended after it.) Any other change (filter/state reset,
  // delete, pin refetch) follows the server again.
  const sigRef = useRef<string>('');
  const prevIdsRef = useRef<number[]>([]);
  const ids = rows.map((r) => r.id);
  const sig = ids.join(',');
  if (sigRef.current !== sig) {
    const prev = prevIdsRef.current;
    const isAppend = ids.length > prev.length && prev.every((id, i) => ids[i] === id);
    sigRef.current = sig;
    prevIdsRef.current = ids;
    if (!isAppend && orderIds !== null) setOrderIds(null);
  }

  const effectiveRows = (() => {
    if (!orderIds) return rows;
    const byId = new Map(rows.map((r) => [r.id, r]));
    const ordered = orderIds.map((id) => byId.get(id)).filter(Boolean) as IDashboardProject[];
    // Include any rows not in the override (e.g. freshly appended) at the end.
    const seen = new Set(orderIds);
    const extra = rows.filter((r) => !seen.has(r.id));
    return [...ordered, ...extra];
  })();

  const dragProps = useCallback(
    (id: number) => ({
      draggable: !disabled,
      onDragStart: (e: React.DragEvent) => {
        if (disabled) return;
        dragIdRef.current = id;
        setDraggingId(id);
        e.dataTransfer.effectAllowed = 'move';
        // Some browsers require data to be set for drag to start.
        try {
          e.dataTransfer.setData('text/plain', String(id));
        } catch {
          /* noop */
        }
      },
      onDragOver: (e: React.DragEvent) => {
        if (disabled || dragIdRef.current == null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDragEnter: (e: React.DragEvent) => {
        if (disabled || dragIdRef.current == null) return;
        e.preventDefault();
        const dragId = dragIdRef.current;
        if (dragId === id) return;
        // Reorder live: move dragId to the position of the hovered row.
        setOrderIds((prev) => {
          const current = prev ?? effectiveRows.map((r) => r.id);
          const from = current.indexOf(dragId);
          const to = current.indexOf(id);
          if (from === -1 || to === -1 || from === to) return current;
          const next = [...current];
          next.splice(from, 1);
          next.splice(to, 0, dragId);
          return next;
        });
      },
      onDrop: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
      },
      onDragEnd: () => {
        const dragId = dragIdRef.current;
        dragIdRef.current = null;
        setDraggingId(null);
        if (disabled || dragId == null) return;
        // Commit the final order. Build 1-indexed dashboard_order entries.
        setOrderIds((prev) => {
          if (!prev) return prev;
          const entries = prev.map((pid, i) => ({ project_id: pid, dashboard_order: i + 1 }));
          // Only commit when the order actually differs from the server order
          // (parity: no-op if init == changed) and there are ≥2 rows.
          const serverIds = rows.map((r) => r.id);
          const changed = prev.some((pid, i) => serverIds[i] !== pid);
          if (changed && entries.length >= 2) {
            onCommit(entries.sort((a, b) => a.dashboard_order - b.dashboard_order));
          }
          return prev;
        });
      },
    }),
    [disabled, effectiveRows, rows, onCommit]
  );

  return { rows: effectiveRows, dragProps, draggingId };
}
